import { notify } from "@kyvg/vue3-notification";

const NOTIFICATION_DURATION = 3500;

const isNumber = numberLike => ( Object.prototype.toString.call( numberLike ) === "[object Number]" );

/**
 * Creates an object with higher-order functions to display different kinds
 * of alerts. Each function accepts a translation key and an arguments object
 * used in translation.
 * @param {String} translationNamespace
 * @param {Function} translate - translation function that resolves a translation
 *   key and an args object to generate a message (e.g., i18n.global.t)
 * @returns { success:Function, error:Function, info:Function, clean:Function }
 * @code
 * const { success, error, info, clean } = alertFactory( "slice-foo.stores.MyStore", i18n.t );
 * success( "saveItemConfirmation", null, { duration: 5000, } );
 * error( "saveItemError", e );
 * info( "notifyReady" )
 * clean( { group: "notificationGroupToClean" } );
 */
export default function alertsFactory( translationNamespace, translate ) {
	const _notify = ( type, key, args, options ) => {
		let notificationOptions = options;

		// backwards compatibility for invocations that
		// pass the duration as options
		if ( isNumber( options ) ) {
			notificationOptions = {
				duration: options,
			};
		}

		if ( !notificationOptions?.duration ) {
			notificationOptions.duration = NOTIFICATION_DURATION;
		}

		notify( {
			...notificationOptions,
			text: translate( `${ translationNamespace }.${ key }`, args ),
			type,
		} );
	};

	const success = ( key, args = {}, options = {} ) =>
		_notify( "success", key, args, options );

	const info = ( key, args = {}, options = {} ) =>
		_notify( "info", key, args, options );

	const warn = ( key, args = {}, options = {} ) =>
		_notify( "warn", key, args, options );

	const error = ( key, args = {}, options = {}, logToConsole = true ) => {
		if ( logToConsole && args ) {
			// eslint-disable-next-line no-console
			console.error( args );
		}
		// TODO: FIXME
		// Errors thrown from our Axios calls bury the actual Error object within
		// the thrown object (because reasons!), so we iterate here until we find it.
		while ( args && args.error ) {
			args = args.error;
		}
		return _notify( "error", key, args, options );
	};

	/** @param {Object.<group:String>} options */
	const clean = ( options = { group: "", } ) => {
		notify( {
			...options,
			clean: true,
		} );
	};

	return {
		success,
		error,
		info,
		warn,
		clean,
	};
}
